import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../services/apiClient';
import { sequenceService } from '../../services/sequenceService';
import { emailService, type EmailAccountConnection } from '../../services/emailService';
import type { BulkEnrollResultItem, EnrollBlockReason, OutreachSequence } from '../../types/sequence';
import { blockReasonMessage, channelLabel, planEnglish } from './copy';

interface Props {
  contacts: { type: 'client' | 'lead'; id: string }[];
  onClose: () => void;
  onDone?: () => void;
}

const CAP = 50;

const BulkEnrollSequenceModal: React.FC<Props> = ({ contacts, onClose, onDone }) => {
  const [sequences, setSequences] = useState<OutreachSequence[]>([]);
  const [mailboxes, setMailboxes] = useState<EmailAccountConnection[]>([]);
  const [sequenceId, setSequenceId] = useState('');
  const [mailboxId, setMailboxId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<{ enrolled: number; results: BulkEnrollResultItem[] } | null>(null);

  const overCap = contacts.length > CAP;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [seqs, accounts] = await Promise.all([
          sequenceService.listSequences(),
          emailService.listAccounts(),
        ]);
        if (!mounted) return;
        setSequences(seqs);
        setMailboxes(accounts);
        if (seqs[0]) setSequenceId(seqs[0].id);
        const primary = accounts.find((a) => a.is_primary) ?? accounts[0];
        if (primary) setMailboxId(primary.id);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selected = useMemo(() => sequences.find((s) => s.id === sequenceId) ?? null, [sequences, sequenceId]);

  const skipBreakdown = useMemo(() => {
    if (!results) return [] as { reason: EnrollBlockReason; count: number }[];
    const counts = new Map<EnrollBlockReason, number>();
    results.results
      .filter((r) => r.status === 'skipped' && r.reason)
      .forEach((r) => counts.set(r.reason!, (counts.get(r.reason!) ?? 0) + 1));
    return Array.from(counts.entries()).map(([reason, count]) => ({ reason, count }));
  }, [results]);

  const start = async () => {
    if (!sequenceId || overCap) return;
    setSubmitting(true);
    try {
      const res = await sequenceService.enrollBulk(sequenceId, {
        contacts: contacts.map((c) => ({ contact_type: c.type, contact_id: c.id })),
        email_account_id: mailboxId || undefined,
      });
      setResults(res);
      onDone?.();
    } catch (err) {
      setResults({
        enrolled: 0,
        results: [{ contact_id: '', status: 'skipped', reason: 'already_enrolled' }],
      });
      if (err instanceof ApiError && err.status === 400) {
        // Surface cap / validation errors via the results panel below.
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-fg">
            Start a sequence for {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="btn-surface-muted rounded-lg px-2 py-1 text-sm">
            ✕
          </button>
        </div>

        {results ? (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-fg">
              {results.enrolled} started
              {results.results.length - results.enrolled > 0
                ? `, ${results.results.length - results.enrolled} skipped`
                : ''}
              .
            </p>
            {skipBreakdown.length > 0 ? (
              <ul className="space-y-1 rounded-xl border border-border bg-surface-muted p-3 text-sm text-fg-muted">
                {skipBreakdown.map(({ reason, count }) => (
                  <li key={reason}>
                    {count} · {blockReasonMessage(reason)}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="btn-primary rounded-xl px-5 py-2 text-sm">
                Done
              </button>
            </div>
          </div>
        ) : loading ? (
          <p className="mt-6 text-sm text-fg-muted">Loading…</p>
        ) : mailboxes.length === 0 ? (
          <div className="mt-6 rounded-xl border border-border bg-surface-muted p-4 text-sm text-fg-muted">
            No mailbox is connected. Connect one to start sending.
            <div className="mt-3">
              <Link to="/settings/email" className="btn-primary rounded-xl px-4 py-2 text-sm">
                Connect a mailbox
              </Link>
            </div>
          </div>
        ) : sequences.length === 0 ? (
          <div className="mt-6 rounded-xl border border-border bg-surface-muted p-4 text-sm text-fg-muted">
            You have no sequences yet.
            <div className="mt-3">
              <Link to="/sequences/new" className="btn-primary rounded-xl px-4 py-2 text-sm">
                Create a sequence
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {overCap ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                Select {CAP} or fewer contacts to start a sequence in bulk. You have {contacts.length}.
              </div>
            ) : null}

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-fg">Sequence</span>
              <select
                value={sequenceId}
                onChange={(e) => setSequenceId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
              >
                {sequences.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-fg">Send from</span>
              <select
                value={mailboxId}
                onChange={(e) => setMailboxId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
              >
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.email_address}
                    {m.is_primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </label>

            {selected ? (
              <div className="rounded-xl border border-border bg-surface-muted p-3">
                <p className="text-sm font-medium text-fg">{planEnglish(selected.steps)}</p>
                <p className="mt-1 text-xs text-fg-muted">
                  Contacts already in a sequence, on your do-not-contact list, or without an email are skipped
                  with a reason.
                </p>
                <ol className="mt-2 space-y-1 text-sm text-fg-muted">
                  {selected.steps.map((step, i) => (
                    <li key={i}>
                      {i + 1}. {channelLabel(step.channel)} ·{' '}
                      {i === 0 ? 'immediately' : `+${step.delay_business_days} business days`}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary rounded-xl px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={start}
                disabled={submitting || overCap || !sequenceId}
                className="btn-primary rounded-xl px-5 py-2 text-sm disabled:opacity-50"
              >
                {submitting ? 'Starting…' : 'Start sequence'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEnrollSequenceModal;
